import type MockAdapter from 'axios-mock-adapter'
import { auditFieldsForCreate, auditFieldsForUpdate, currentUserId, newId } from '../engine/context'
import { type SubmitWorkflowPayload, setSubmission, WorkflowError } from '../engine/workflowEngine'
import { deleteRow, findRow, insertRow, readTable, updateRow, writeTable } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'
import { closeReturnsAfterPutaway, logWarehouseId } from './c07.shared'
import { notify, userName, wmsRoute } from './notification.shared'
import { binsOfRm } from './rackScope'
import { toErrorResponse } from './workflow.handler'

/**
 * Endpoint custom C04 layar operator mobile (IRM-05 QC RM — Operator, IRM-07 Putaway RM — Operator):
 * - `POST /api/v1/wms/qc-rm-karantina/workflow/qc-inspection-results-submission` (BE M01.C04.P02.F01,
 *   WORKFLOW-CUSTOM WF-QC-01 `ACT_QC_RECORD`): v7.50 — satu simpan per pallet = DRAF (`QC_PENDING_APPROVAL`),
 *   kode Good/NG dipesan, Admin WH RM diberi notifikasi (prototype `qcSimpanHasil`).
 * - `POST /api/v1/wms/inbound-receiving/qc-package/approve-print | return` (v7.50 R-05…R-08): Admin WH RM menyetujui
 *   & mencetak label per pallet (unit Good/NG, tiket NG, karantina ditutup, SATU tugas putaway untuk operator QC —
 *   prototype `qcSetujuiCetak`) atau mengembalikannya ke operator dengan alasan.
 * - `POST /api/v1/wms/putaway-execution/pick-item | bring-to-rack | place-item | rack-full | finish-task`
 *   (BE M01.C04.P04.F01 ditulis ulang sebagai CUSTOM-NESTJS pada `rm_putaway_task_item`, laporan 06 §4.4):
 *   tahap 1 ambil di Area QC (ZQ-RM; tembakau tanpa gerbang, v7.50 R-01), tahap 2 taruh di rack; aturan BR-04 (satu posisi satu material,
 *   kategori rak hard-block, NG hanya QN-NG); `ACT_QC_STORE_ALL` dijalankan sistem saat semua unit
 *   assignment sudah berdiri; pallet dilepas (EMPTY) saat seluruh isi pallet selesai ditempatkan.
 * `ACT_QC_START` (scan QR pallet) memakai `POST /api/workflow/submission/v2` standar per assignment.
 */

const idOf = (value: unknown) =>
  typeof value === 'object' && value !== null ? String((value as { id?: unknown }).id ?? '') : String(value ?? '')

const ok = (data: unknown) => [200, { statusCode: 200, success: true, data }]

function fail(message: string, code = 'ERR_VALIDATION_FAILED', status = 400): never {
  throw new WorkflowError(message, code, status)
}

const pad = (value: number, width: number) => String(value).padStart(width, '0')

const NG_ZONE = 'QN-NG'
const QC_AREA_ZONE = 'ZQ-RM'

/* ------------------------------------------------------------ referensi */

const statusCodeOf = (row: LocalRow | undefined) => String(findRow('wml_status', idOf(row?.status_id))?.code ?? '')

function roleCodesOf(userId: string): string[] {
  return readTable('mt_privileges')
    .filter(row => idOf(row.user) === userId)
    .map(row => String(findRow('mt_roles', idOf(row.role))?.code ?? ''))
}

function requireOperatorOwner(ownerId: unknown) {
  const me = currentUserId()
  if (!roleCodesOf(me).includes('operator_wh_rm') || idOf(ownerId) !== me)
    fail('Hanya operator pemilik tugas yang bisa scan.', 'ERR_UNAUTHORIZED_ROLE', 403)
}

const zoneByCode = (code: string) => readTable('warehouse_zone').find(zone => String(zone.code).toUpperCase() === code)
const binByCode = (code: string) => readTable('mt_rack_bin').find(bin => String(bin.code).toUpperCase() === code)
const rackOf = (bin: LocalRow | undefined) => (bin ? findRow('mt_rack', idOf(bin.rack_id)) : undefined)

/** `PREFIX-YYMMDD-nnn`, urut per hari (sama dengan handler inbound). */
function nextDocNo(prefix: string, collection: string, field: string, date = new Date()) {
  const day = `${String(date.getFullYear()).slice(2)}${pad(date.getMonth() + 1, 2)}${pad(date.getDate(), 2)}`
  const pattern = new RegExp(`^${prefix}-${day}-(\\d{3})`)
  const highest = readTable(collection)
    .map(row => pattern.exec(String(row[field] ?? '')))
    .reduce((max, match) => (match ? Math.max(max, Number(match[1])) : max), 0)
  return `${prefix}-${day}-${pad(highest + 1, 3)}`
}

/**
 * Kode unit 4 segmen `{MAT}:{YYYYMMDD}:{NN}:{nnnn}` dari nomor batch `{MAT}-{YYMMDD}-{NN}` — nnnn naik per
 * batch (prototype v7.43 `kodeUnitBatch`). `reserved` = kode yang sudah dipesan dalam satu transaksi; kode yang dipesan
 * draf QC (`rm_qc_assignment.good_unit_code` / `ng_unit_code`, v7.50 R-09) selalu dilompati.
 */
export function nextUnitCode(batchNo: string, reserved: string[] = []) {
  const match = /^(.*)-(\d{6})-(\d{2})$/.exec(batchNo)
  const [code, day, seq] = match ? match.slice(1) : [batchNo, '000000', '01']
  const prefix = `${code}:20${day}:${seq}:`
  const used = [...readTable('rm_unit').map(unit => String(unit.qr_code ?? '')), ...reserved, ...reservedDraftCodes()]
    .filter(value => value.startsWith(prefix))
    .map(value => Number(value.slice(prefix.length)))
  return `${prefix}${pad(Math.max(0, ...used) + 1, 4)}`
}

/* --------------------------------------------------- rekomendasi rak */

/** Material lain yang sudah menempati satu posisi (BR-04: satu posisi satu material). */
function foreignMaterialAt(binId: string, materialId: string) {
  const unit = readTable('rm_unit').find(
    row => idOf(row.rack_bin_id) === binId && row.unit_status === 'IN_STOCK' && idOf(row.material_id) !== materialId
  )
  return unit ? findRow('mt_material', idOf(unit.material_id)) : undefined
}

/**
 * Rekomendasi posisi rak (prototype v4.8 `recommendFreeLoc`): 1) posisi yang sudah berisi material yang
 * sama → konsolidasi; 2) posisi kosong di rack yang sudah menampung material itu; 3) posisi kosong yang
 * kategorinya cocok; 4) posisi kosong tanpa pembatasan. "Terpakai" termasuk posisi yang sudah
 * direkomendasikan ke item putaway yang belum selesai.
 */
export function recommendRackBin(materialId: string, exclude: string[] = []): LocalRow | null {
  const material = findRow('mt_material', materialId)
  const category = idOf(material?.category_id)
  const usedBy = new Map<string, string>()
  readTable('rm_unit').forEach(unit => {
    if (unit.unit_status === 'IN_STOCK' && unit.rack_bin_id) usedBy.set(idOf(unit.rack_bin_id), idOf(unit.material_id))
  })
  readTable('rm_putaway_task_item').forEach(item => {
    const binId = idOf(item.recommended_rack_bin_id)
    if (!binId || usedBy.has(binId) || Number(item.qty_placed ?? 0) > 0) return
    const unit = findRow('rm_unit', idOf(item.unit_id))
    if (unit) usedBy.set(binId, idOf(unit.material_id))
  })
  const racks = new Map(readTable('mt_rack').map(rack => [rack.id, rack]))
  const bins = binsOfRm()
    .filter(bin => bin.is_active !== false && !exclude.includes(bin.id))
    .sort((a, b) => String(a.code).localeCompare(String(b.code), 'id', { numeric: true }))
  const restrictionOf = (bin: LocalRow) => idOf(racks.get(idOf(bin.rack_id))?.category_restriction_id)
  const categoryOk = (bin: LocalRow) => !restrictionOf(bin) || !category || restrictionOf(bin) === category
  const same = bins.find(bin => categoryOk(bin) && usedBy.get(bin.id) === materialId)
  if (same) return same
  const racksWithMaterial = new Set(bins.filter(bin => usedBy.get(bin.id) === materialId).map(bin => idOf(bin.rack_id)))
  return (
    bins.find(bin => categoryOk(bin) && !usedBy.has(bin.id) && racksWithMaterial.has(idOf(bin.rack_id))) ??
    bins.find(bin => categoryOk(bin) && !usedBy.has(bin.id)) ??
    bins.find(bin => !restrictionOf(bin) && !usedBy.has(bin.id)) ??
    null
  )
}

/* --------------------------------------------------------- hasil QC */

type QcResultInput = {
  assignment_id?: string
  qty_good?: number
  qty_ng?: number
  reject_type_ids?: string[]
  note?: string
}

function txLog(logType: string, pageCode: string, action: string, docRef: string, detail: string) {
  insertRow('tx_log', {
    ...auditFieldsForCreate(),
    id: newId(),
    ts: new Date().toISOString(),
    log_type: logType,
    page_code: pageCode,
    action,
    doc_ref: docRef,
    detail,
    warehouse_id: logWarehouseId(pageCode),
    user_id: currentUserId()
  })
}

/* Workflow WF-QC-01 — id aksi & actor dicari per nama (aksi sistem / admin tidak lewat action-config FE). */
function qcWorkflowRefs(actionName: string, actorCode: string) {
  const workflow = readTable('wml_workflow').find(row => row.code === 'WF-QC-01')
  const action = readTable('wml_action').find(row => idOf(row.workflow_id) === workflow?.id && row.name === actionName)
  const actor = readTable('wml_actor').find(row => idOf(row.workflow_id) === workflow?.id && row.code === actorCode)
  if (!workflow || !action || !actor) fail(`Konfigurasi ${actionName} tidak ditemukan.`, 'ERR_NOT_FOUND', 500)
  return { workflow_id: String(workflow?.id), action_id: String(action?.id), actor_id: String(actor?.id) }
}

/**
 * Transaksi lokal: tabel yang disentuh disalin dulu (baris immutable, cukup referensinya) dan dipulihkan bila ada
 * langkah yang gagal — termasuk transisi workflow yang sudah jalan untuk assignment sebelumnya dalam paket yang sama.
 */
const QC_TX_TABLES = [
  'rm_unit',
  'ng_ticket',
  'rm_putaway_task',
  'rm_putaway_task_item',
  'rm_qc_assignment',
  'rm_quarantine_entry',
  'wxl_submission',
  'wxl_submission_log',
  'notification',
  'tx_log'
]

function inTransaction<T>(run: () => T): T {
  const snapshot = QC_TX_TABLES.map(collection => [collection, readTable(collection)] as const)
  try {
    return run()
  } catch (error) {
    snapshot.forEach(([collection, rows]) => writeTable(collection, rows))
    throw error
  }
}

/** Kode yang sudah dipesan draf QC lain (R-09): kode Good / NG tidak boleh dipakai dua kali selama draf menunggu. */
const reservedDraftCodes = (excludeAssignmentIds: string[] = []) =>
  readTable('rm_qc_assignment')
    .filter(row => !excludeAssignmentIds.includes(String(row.id)))
    .flatMap(row => [row.good_unit_code, row.ng_unit_code])
    .filter(Boolean)
    .map(String)

const palletOfAssignment = (assignment: LocalRow) =>
  idOf(findRow('rm_quarantine_entry', idOf(assignment.quarantine_entry_id))?.pallet_id)

/** Satu paket persetujuan = seluruh assignment satu pallet milik operator yang sama (prototype `qcPaketKey`). */
export function packageAssignments(palletId: string, operatorId: string, status?: string) {
  return readTable('rm_qc_assignment').filter(
    row =>
      palletOfAssignment(row) === palletId &&
      idOf(row.operator_id) === operatorId &&
      (!status || statusCodeOf(row) === status)
  )
}

const lineOfAssignment = (assignment: LocalRow) => {
  const entry = findRow('rm_quarantine_entry', idOf(assignment.quarantine_entry_id))
  return findRow('rm_arrival_line', idOf(entry?.arrival_line_id))
}

/**
 * Pesan kode Good / NG saat draf disimpan (prototype `qcPesanKodeNg`): kode lama assignment dipakai lagi lebih dulu
 * (draf yang dikembalikan lalu disimpan ulang tetap memakai kode yang sama); tanpa bagian Good, unit NG memakai kode
 * pertama itu sendiri. Kode baru melompati unit yang sudah terbit dan kode yang dipesan draf lain.
 */
function reserveCodes(assignment: LocalRow, good: number, ng: number, takenInSave: string[]) {
  const previous = [assignment.good_unit_code, assignment.ng_unit_code].filter(Boolean).map(String)
  const batchNo = String(lineOfAssignment(assignment)?.batch_no ?? '')
  const take = () => {
    const code = previous.shift() ?? nextUnitCode(batchNo, takenInSave)
    takenInSave.push(code)
    return code
  }
  if (good > 0) {
    const goodCode = take()
    return { good_unit_code: goodCode, ng_unit_code: ng > 0 ? take() : null }
  }
  return { good_unit_code: null, ng_unit_code: ng > 0 ? take() : null }
}

/**
 * IRM-05 "Simpan Hasil QC" (v7.50 R-04): menyimpan DRAF satu pallet — `ACT_QC_RECORD` per assignment
 * (`QC_IN_PROGRESS → QC_PENDING_APPROVAL`). Yang ditulis hanya Good / NG / alasan + kode yang dipesan; unit, QR, label,
 * tiket NG, penutupan karantina dan putaway baru terbit saat Admin WH RM "Setujui & Cetak Label" (`issueQcPackage`).
 */
export function saveQcDraft(body: SubmitWorkflowPayload) {
  const payload = (body.native_document_payload?.payload ?? {}) as { pallet_id?: string; results?: QcResultInput[] }
  const results = payload.results ?? []
  if (!results.length) fail('Belum ada material yang dinilai.')
  const me = currentUserId()
  const pallet = findRow('pallet', payload.pallet_id)
  if (!pallet) fail('Pallet tidak ditemukan.', 'ERR_NOT_FOUND', 404)

  const assignments = results.map(result => {
    const assignment = findRow('rm_qc_assignment', result.assignment_id)
    if (!assignment) fail('Assignment tidak ditemukan.', 'ERR_NOT_FOUND', 404)
    requireOperatorOwner(assignment?.operator_id)
    if (statusCodeOf(assignment) !== 'QC_IN_PROGRESS')
      fail('Scan QR pallet dulu — isi pallet baru terbuka setelah QR pallet cocok.', 'ERR_WORKFLOW_STATE_MISMATCH', 422)
    const entry = findRow('rm_quarantine_entry', idOf(assignment?.quarantine_entry_id))
    if (!entry || idOf(entry.pallet_id) !== pallet?.id) fail('Material bukan bagian pallet ini.')
    const good = Number(result.qty_good)
    const ng = Number(result.qty_ng)
    const qty = Number(assignment?.qty)
    if (!Number.isInteger(good) || !Number.isInteger(ng) || good < 0 || ng < 0 || good + ng !== qty)
      fail(`Good + NG harus pas ${qty} ${unitNameOf(entry)}.`)
    const reasons = (result.reject_type_ids ?? [])
      .map(id => findRow('mt_reject_type', id))
      .filter(Boolean) as LocalRow[]
    if (ng > 0 && !reasons.length) fail('Pilih minimal satu alasan Not Good.')
    return { assignment: assignment as LocalRow, entry, good, ng, reasons, note: String(result.note ?? '').trim() }
  })

  /* Satu pallet disimpan sekaligus: seluruh assignment operator ini di pallet tersebut harus ikut. */
  const mine = packageAssignments(String(pallet?.id), me, 'QC_IN_PROGRESS')
  const missing = mine.filter(row => !assignments.some(item => item.assignment.id === row.id))
  if (missing.length) fail(`${missing.length} material belum dinilai.`)

  const taken: string[] = []
  const saved = inTransaction(() =>
    assignments.map(({ assignment, good, ng, reasons, note }) => {
      const codes = reserveCodes(assignment, good, ng, taken)
      const reasonText =
        ng > 0 ? reasons.map(reason => String(reason.name)).join(', ') + (note ? ` — ${note}` : '') : ''
      const result = setSubmission({
        ...body,
        submission_id: String(assignment.submission_id),
        entity_id: assignment.id,
        native_document_payload: {
          path: 'rm_qc_assignment',
          payload: {
            qc_status: 'PENDING_APPROVAL',
            qty_good: good,
            qty_ng: ng,
            ng_reason: reasonText || null,
            ng_reason_ids: ng > 0 ? reasons.map(reason => reason.id) : [],
            ...codes
          }
        }
      })
      return { assignment, good, ng, codes, submissionId: result.submission_id }
    })
  )

  const totalGood = saved.reduce((sum, item) => sum + item.good, 0)
  const totalNg = saved.reduce((sum, item) => sum + item.ng, 0)
  txLog(
    'RM QC',
    'IRM-05',
    'qc-hasil',
    String(pallet?.code),
    `Pallet ${pallet?.code} — ${saved.length} material dinilai: ${totalGood} Good / ${totalNg} NG — hasil dikirim, menunggu persetujuan Admin / Supervisor WH RM`
  )
  const arrival = findRow('rm_arrival', idOf(lineOfAssignment(saved[0].assignment)?.arrival_id))
  notify({
    to: { roles: [...QC_APPROVER_ROLES] },
    type: 'QC_DRAFT_SUBMITTED',
    title: `Hasil QC pallet ${pallet?.code} menunggu persetujuan`,
    body: `${userName(me)} menyimpan hasil QC pallet ${pallet?.code} (${totalGood} Good / ${totalNg} Not Good) — buka Detail Kedatangan, lalu Setujui & Cetak Label atau Kembalikan.`,
    link: arrival
      ? wmsRoute(`procurement/inbound-receiving/arrival/${encodeURIComponent(String(arrival.arrival_no))}`)
      : wmsRoute('procurement/inbound-receiving'),
    ref: { type: 'pallet', id: String(pallet?.id) },
    severity: 'warning'
  })
  return {
    pallet_code: pallet?.code,
    status: 'QC_PENDING_APPROVAL',
    totals: { good: totalGood, ng: totalNg, materials: saved.length },
    reserved_codes: saved.map(item => ({ assignment_id: item.assignment.id, ...item.codes })),
    submission_ids: saved.map(item => item.submissionId)
  }
}

type IssuedLabel = {
  unit_qr: string
  flag: 'REGULAR' | 'NG'
  qty: number
  unit: string
  material_code: string
  material_name: string
  batch_no: string
  recommended: string | null
  ticket_no: string | null
}

/**
 * Terbitkan satu paket yang disetujui (blok penerbitan lama `recordQcResult`, dipindah utuh): unit Good / NG dengan
 * kode yang sudah dipesan, tiket NG, karantina ditutup, `ACT_QC_APPROVE_PRINT` per assignment, dan SATU tugas putaway
 * untuk operator yang melakukan QC (R-07). Dipanggil di dalam `inTransaction`.
 */
function issueQcPackage(assignments: LocalRow[], operatorId: string) {
  const refs = qcWorkflowRefs('ACT_QC_APPROVE_PRINT', qcApproverActor())
  const reservedCodes: string[] = []
  const now = new Date()
  const taskNo = nextDocNo('PWR', 'rm_putaway_task', 'task_no', now)
  const taskId = newId()
  const labels: IssuedLabel[] = []
  let itemCount = 0

  assignments.forEach(assignment => {
    const entry = findRow('rm_quarantine_entry', idOf(assignment.quarantine_entry_id)) as LocalRow
    const line = findRow('rm_arrival_line', idOf(entry.arrival_line_id))
    const material = findRow('mt_material', idOf(line?.material_id)) as LocalRow
    const arrival = findRow('rm_arrival', idOf(line?.arrival_id))
    const batchNo = String(line?.batch_no ?? '')
    const perBig = Number(material.default_purchase_unit_qty ?? 1)
    const unitName = unitNameOf(entry)
    const good = Number(assignment.qty_good ?? 0)
    const ng = Number(assignment.qty_ng ?? 0)
    const reasonText = String(assignment.ng_reason ?? '')
    const makeUnit = (qty: number, flag: 'REGULAR' | 'NG', reserved: unknown) => {
      const qr = String(reserved ?? '') || nextUnitCode(batchNo, reservedCodes)
      if (readTable('rm_unit').some(unit => unit.qr_code === qr))
        fail(`Kode ${qr} sudah dipakai unit lain — simpan ulang hasil QC.`, 'ERR_DUPLICATE_CODE', 409)
      reservedCodes.push(qr)
      return insertRow('rm_unit', {
        ...auditFieldsForCreate(),
        id: newId(),
        uid: `UNIT-${pad(readTable('rm_unit').length + 1, 6)}`,
        qr_code: qr,
        material_id: material.id,
        batch_no: batchNo,
        supplier_id: arrival?.supplier_id ?? null,
        po_no: line?.po_no ?? null,
        qty_small: qty * perBig,
        rack_bin_id: null,
        warehouse_zone_id: null,
        flag,
        unit_status: flag === 'NG' ? 'NG_AWAITING_PLACEMENT' : 'AWAITING_PUTAWAY',
        qc_date: now.toISOString(),
        arrival_line_id: line?.id ?? null,
        quarantine_entry_id: entry.id,
        unit_big_snapshot: unitName,
        small_per_big_snapshot: perBig,
        is_leftover_wip: false,
        stored_at: null
      })
    }
    const goodUnit = good > 0 ? makeUnit(good, 'REGULAR', assignment.good_unit_code) : null
    const ngUnit = ng > 0 ? makeUnit(ng, 'NG', assignment.ng_unit_code) : null
    const reco = goodUnit ? recommendRackBin(material.id) : null
    const ngZone = zoneByCode(NG_ZONE)
    const ticket = ngUnit
      ? insertRow('ng_ticket', {
          ...auditFieldsForCreate(),
          id: newId(),
          ticket_no: nextDocNo('NGT', 'ng_ticket', 'ticket_no', now),
          unit_id: ngUnit.id,
          supplier_id: arrival?.supplier_id ?? null,
          qty: ng,
          parameter_result: reasonText,
          placement_rack_bin_id: null,
          placement_warehouse_zone_id: null,
          ticket_status: 'OPEN',
          quarantine_entry_id: entry.id,
          qc_assignment_id: assignment.id
        })
      : null
    ;[goodUnit, ngUnit].forEach(unit => {
      if (!unit) return
      const isNg = unit.flag === 'NG'
      itemCount += 1
      insertRow('rm_putaway_task_item', {
        ...auditFieldsForCreate(),
        id: newId(),
        task_id: taskId,
        unit_id: unit.id,
        recommended_rack_bin_id: isNg ? null : (reco?.id ?? null),
        recommended_warehouse_zone_id: isNg ? (ngZone?.id ?? null) : (zoneOfBin(reco)?.id ?? null),
        actual_rack_bin_id: null,
        actual_warehouse_zone_id: null,
        qty_assigned: isNg ? ng : good,
        qty_placed: 0,
        qty_short: null,
        picked_at: null,
        override_reason: null
      })
      labels.push({
        unit_qr: String(unit.qr_code),
        flag: unit.flag as 'REGULAR' | 'NG',
        qty: isNg ? ng : good,
        unit: unitName,
        material_code: String(material.code),
        material_name: String(material.name),
        batch_no: batchNo,
        recommended: isNg ? NG_ZONE : ((reco?.code as string | undefined) ?? null),
        ticket_no: isNg ? String(ticket?.ticket_no ?? '') : null
      })
    })

    setSubmission({
      ...refs,
      submission_id: String(assignment.submission_id),
      entity_id: assignment.id,
      native_document_payload: {
        path: 'rm_qc_assignment',
        payload: {
          qc_status: 'QC_DONE',
          done_at: now.toISOString(),
          unit_id: goodUnit?.id ?? null,
          ng_unit_id: ngUnit?.id ?? null
        }
      }
    })

    /* Karantina tuntas bila seluruh qty sudah DITERBITKAN (draf lain yang belum disetujui tidak dihitung). */
    const issued = readTable('rm_qc_assignment')
      .filter(
        row => idOf(row.quarantine_entry_id) === entry.id && ['QC_DONE', 'QC_IN_STORAGE'].includes(statusCodeOf(row))
      )
      .reduce((sum, row) => sum + Number(row.qty_good ?? 0) + Number(row.qty_ng ?? 0), 0)
    const current = findRow('rm_quarantine_entry', entry.id) as LocalRow
    const completed = issued >= Number(current.qty)
    updateRow('rm_quarantine_entry', entry.id, {
      qty_good: Number(current.qty_good ?? 0) + good,
      qty_ng: Number(current.qty_ng ?? 0) + ng,
      is_qc_completed: completed,
      quarantine_status: completed ? 'QC_COMPLETED' : 'IN_QUARANTINE',
      qc_completed_at: completed ? now.toISOString() : null,
      ...auditFieldsForUpdate()
    })
  })

  if (itemCount)
    insertRow('rm_putaway_task', {
      ...auditFieldsForCreate(),
      id: taskId,
      task_no: taskNo,
      operator_id: operatorId,
      task_status: 'ASSIGNED',
      phase: 'PICK',
      note: null
    })
  return { task_no: itemCount ? taskNo : null, task_id: itemCount ? taskId : null, labels }
}

type PackageBody = { pallet_id?: string; operator_id?: string; reason?: string }

/** Peran yang boleh Setujui & Cetak Label / Kembalikan ke Operator (keputusan user 2026-10-21 #2). Kadiv hanya melihat. */
export const QC_APPROVER_ROLES = ['admin_wh_rm', 'wh_rm_supervisor'] as const

/** Aktor workflow WF-QC-01 untuk penyetuju yang sedang login (Admin → ACT_QC_ADMIN_RM, Supervisor → ACT_QC_SPV_RM). */
function qcApproverActor() {
  const roles = roleCodesOf(currentUserId())
  if (roles.includes('admin_wh_rm')) return 'ACT_QC_ADMIN_RM'
  if (roles.includes('wh_rm_supervisor')) return 'ACT_QC_SPV_RM'
  return fail(
    'Hanya Admin WH RM dan Supervisor WH RM yang boleh menyetujui atau mengembalikan hasil QC.',
    'ERR_UNAUTHORIZED_ROLE',
    403
  )
}

function requireQcApprover() {
  qcApproverActor()
}

function requirePackage(body: PackageBody) {
  requireQcApprover()
  const pallet = findRow('pallet', body.pallet_id)
  if (!pallet) fail('Pallet tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  const operatorId = String(body.operator_id ?? '')
  if (!findRow('directus_users', operatorId)) fail('Operator QC tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  return { pallet: pallet as LocalRow, operatorId }
}

/** Label dari unit yang sudah terbit (Lihat Label, dan jawaban idempoten approve-print). */
export function issuedLabelsOf(assignments: LocalRow[]): IssuedLabel[] {
  return assignments.flatMap(assignment => {
    const entry = findRow('rm_quarantine_entry', idOf(assignment.quarantine_entry_id))
    const unitName = entry ? unitNameOf(entry) : ''
    return [assignment.unit_id, assignment.ng_unit_id]
      .map(id => findRow('rm_unit', idOf(id)))
      .filter((unit): unit is LocalRow => Boolean(unit))
      .map(unit => {
        const material = findRow('mt_material', idOf(unit.material_id))
        const isNg = unit.flag === 'NG'
        const item = readTable('rm_putaway_task_item').find(row => idOf(row.unit_id) === unit.id)
        const ticket = isNg ? readTable('ng_ticket').find(row => idOf(row.unit_id) === unit.id) : undefined
        return {
          unit_qr: String(unit.qr_code),
          flag: unit.flag as 'REGULAR' | 'NG',
          qty: Number(isNg ? assignment.qty_ng : assignment.qty_good) || 0,
          unit: unitName,
          material_code: String(material?.code ?? ''),
          material_name: String(material?.name ?? ''),
          batch_no: String(unit.batch_no ?? ''),
          recommended: isNg
            ? NG_ZONE
            : ((findRow('mt_rack_bin', idOf(item?.recommended_rack_bin_id))?.code as string | undefined) ?? null),
          ticket_no: ticket ? String(ticket.ticket_no) : null
        }
      })
  })
}

/**
 * `POST /api/v1/wms/inbound-receiving/qc-package/approve-print` (R-05/R-06): Admin WH RM menekan Cetak di modal stiker →
 * paket disetujui dan diterbitkan dalam satu transaksi. Idempoten: paket yang sudah terbit (cetak ulang setelah PDF
 * gagal) menjawab label yang sama tanpa menerbitkan apa pun lagi.
 */
export function approvePrintQcPackage(body: PackageBody) {
  const { pallet, operatorId } = requirePackage(body)
  const pending = packageAssignments(pallet.id, operatorId, 'QC_PENDING_APPROVAL')
  if (!pending.length) {
    const issued = packageAssignments(pallet.id, operatorId).filter(row =>
      ['QC_DONE', 'QC_IN_STORAGE'].includes(statusCodeOf(row))
    )
    if (issued.length && issued.some(row => row.unit_id || row.ng_unit_id))
      return { already: true, pallet_code: pallet.code, task_no: null, labels: issuedLabelsOf(issued) }
    fail('Hasil QC ini tidak lagi menunggu persetujuan — muat ulang halaman.', 'ERR_WORKFLOW_STATE_MISMATCH', 409)
  }
  const result = inTransaction(() => issueQcPackage(pending, operatorId))
  const good = pending.reduce((sum, row) => sum + Number(row.qty_good ?? 0), 0)
  const ng = pending.reduce((sum, row) => sum + Number(row.qty_ng ?? 0), 0)
  txLog(
    'RM QC',
    'PRC-05',
    'qc-setuju-cetak',
    String(pallet.code),
    `Pallet ${pallet.code} — ${pending.length} material: ${good} Good / ${ng} NG — disetujui & label dicetak admin${result.task_no ? `, tugas penempatan ${result.task_no} untuk ${userName(operatorId)}` : ''}`
  )
  if (result.task_no)
    notify({
      to: { userIds: [operatorId] },
      type: 'PUTAWAY_ASSIGNED',
      title: `Tugas putaway ${result.task_no} untuk Anda`,
      body: `Hasil QC pallet ${pallet.code} disetujui ${userName(currentUserId())} dan label sudah dicetak — buka Putaway RM — Operator.`,
      link: wmsRoute('inbound-rm/putaway-rm-operator', { task: String(result.task_id) }),
      ref: { type: 'rm_putaway_task', id: String(result.task_id) }
    })
  return {
    already: false,
    pallet_code: pallet.code,
    operator_id: operatorId,
    task_no: result.task_no,
    task_id: result.task_id,
    totals: { good, ng, materials: pending.length },
    labels: result.labels
  }
}

/**
 * `POST /api/v1/wms/inbound-receiving/qc-package/return` (R-08): kembalikan seluruh paket ke operator QC dengan alasan
 * wajib — `ACT_QC_RETURN` (`QC_PENDING_APPROVAL → QC_IN_PROGRESS`). Hasil lama tetap di assignment sebagai nilai awal;
 * alasan, pengembali dan waktunya dibaca dari log submission.
 */
export function returnQcPackage(body: PackageBody) {
  const { pallet, operatorId } = requirePackage(body)
  const reason = String(body.reason ?? '').trim()
  if (!reason) fail('Alasan wajib diisi.')
  const pending = packageAssignments(pallet.id, operatorId, 'QC_PENDING_APPROVAL')
  if (!pending.length)
    fail('Hasil QC ini tidak lagi menunggu persetujuan — muat ulang halaman.', 'ERR_WORKFLOW_STATE_MISMATCH', 409)
  const refs = qcWorkflowRefs('ACT_QC_RETURN', qcApproverActor())
  inTransaction(() =>
    pending.forEach(assignment =>
      setSubmission({
        ...refs,
        submission_id: String(assignment.submission_id),
        entity_id: assignment.id,
        reason_text: reason,
        native_document_payload: { path: 'rm_qc_assignment', payload: { qc_status: 'IN_PROGRESS' } }
      })
    )
  )
  txLog(
    'RM QC',
    'PRC-05',
    'qc-kembali',
    String(pallet.code),
    `Hasil QC pallet ${pallet.code} (${pending.length} material) dikembalikan ke ${userName(operatorId)} — alasan: ${reason}`
  )
  notify({
    to: { userIds: [operatorId] },
    type: 'QC_RETURNED',
    title: `Hasil QC pallet ${pallet.code} dikembalikan`,
    body: `${userName(currentUserId())} mengembalikan hasil QC pallet ${pallet.code}. Alasan: ${reason} — buka QC RM — Operator, perbaiki, lalu simpan ulang.`,
    link: wmsRoute('inbound-rm/qc-rm-operator', { task: pallet.id }),
    ref: { type: 'pallet', id: String(pallet.id) },
    severity: 'warning'
  })
  return { pallet_code: pallet.code, operator_id: operatorId, returned: pending.length, reason }
}

const unitNameOf = (entry: LocalRow) => {
  const line = findRow('rm_arrival_line', idOf(entry.arrival_line_id))
  return String(findRow('mt_unit', idOf(line?.unit_id))?.name ?? '')
}

const zoneOfBin = (bin: LocalRow | null) => (bin ? findRow('warehouse_zone', idOf(rackOf(bin)?.zone_id)) : undefined)

/* ------------------------------------------------------------- putaway */

type ItemBody = { task_item_id?: string }

function requireItem(body: ItemBody) {
  const item = findRow('rm_putaway_task_item', body.task_item_id)
  if (!item) fail('Item tugas tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  const task = findRow('rm_putaway_task', idOf(item?.task_id)) as LocalRow
  requireOperatorOwner(task.operator_id)
  const unit = findRow('rm_unit', idOf(item?.unit_id)) as LocalRow
  return { item: item as LocalRow, task, unit }
}

const isPlaced = (item: LocalRow) => Number(item.qty_placed ?? 0) > 0

/* ------------------------------------------------------ tembakau (R-01) */

const TOBACCO_CATEGORY = 'TEMBAKAU'

/**
 * Unit tembakau yang TIDAK lewat karantina/QC (Penerimaan Tembakau, Retur Sisa Tembakau) — prototype `pw.tembakau`.
 * DERIVE dari kategori material + tanpa baris karantina; material tembakau yang datang lewat kedatangan & QC tetap
 * memakai alur Area QC RM.
 */
export function isTobaccoUnit(unit: LocalRow | undefined) {
  if (!unit || unit.quarantine_entry_id) return false
  const material = findRow('mt_material', idOf(unit.material_id))
  const group = material ? findRow('mt_material_group', idOf(material.category_id)) : undefined
  return String(group?.code ?? '').toUpperCase() === TOBACCO_CATEGORY
}

export function isTobaccoTask(taskId: string) {
  const items = readTable('rm_putaway_task_item').filter(item => idOf(item.task_id) === taskId)
  return items.length > 0 && items.every(item => isTobaccoUnit(findRow('rm_unit', idOf(item.unit_id))))
}

const TOBACCO_NOT_QC_AREA = 'Bukan untuk Tembakau — Putaway tembakau tidak melewati Area QC RM (ZQ-RM).'

/**
 * Tahap 1: unit diangkat. RM hasil QC: scan QR unit setelah gerbang ZQ-RM (gerbang dicek di layar). Tembakau: tanpa
 * gerbang — scan stiker barang yang dibawa; kode ZQ-RM ditolak (`scanned_code`, R-01).
 */
export function pickPutawayItem(body: ItemBody & { scanned_code?: string }) {
  const { item, task } = requireItem(body)
  if (
    String(body.scanned_code ?? '')
      .trim()
      .toUpperCase() === QC_AREA_ZONE &&
    isTobaccoTask(task.id)
  )
    fail(`${TOBACCO_NOT_QC_AREA} Scan stiker barang yang Anda bawa.`, 'ERR_VALIDATION_FAILED', 422)
  if (isPlaced(item)) fail('Barang ini sudah tersimpan.', 'ERR_WORKFLOW_STATE_MISMATCH', 422)
  if (item.picked_at) fail('Barang ini sudah diangkat.', 'ERR_WORKFLOW_STATE_MISMATCH', 422)
  if (task.phase === 'PLACE')
    fail('Tahap mengambil sudah ditutup — selesaikan tugas ini dulu.', 'ERR_WORKFLOW_STATE_MISMATCH', 422)
  updateRow('rm_putaway_task_item', item.id, { picked_at: new Date().toISOString(), ...auditFieldsForUpdate() })
  return { task_item_id: item.id, picked: true }
}

/** "Bawa ke Rack": tahap 2 dibuka; sisa yang belum diangkat tetap Pending (catatan wajib bila ada sisa). */
export function bringToRack(body: { task_id?: string; note?: string }) {
  const task = findRow('rm_putaway_task', body.task_id)
  if (!task) fail('Tugas tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  requireOperatorOwner(task?.operator_id)
  const items = readTable('rm_putaway_task_item').filter(item => idOf(item.task_id) === task?.id)
  const picked = items.filter(item => item.picked_at || isPlaced(item))
  if (!picked.length)
    fail(
      isTobaccoTask(String(task?.id))
        ? 'Scan stiker tumpukan yang Anda bawa dulu.'
        : 'Scan tumpukan yang Anda ambil di Area QC RM dulu.'
    )
  const pending = items.filter(item => !item.picked_at && !isPlaced(item))
  const note = String(body.note ?? '').trim()
  if (pending.length && !note) fail('Catatan wajib bila tetap dibawa dengan sisa.')
  updateRow('rm_putaway_task', task?.id as string, {
    phase: 'PLACE',
    task_status: 'IN_PROGRESS',
    note: note || task?.note || null,
    ...auditFieldsForUpdate()
  })
  if (pending.length)
    txLog(
      'RM Putaway',
      'IRM-07',
      'putaway-sisa',
      String(task?.task_no),
      `${pending.length} tumpukan belum diambil — ${note}`
    )
  return { task_id: task?.id, carried: picked.length, pending: pending.length }
}

type PlaceBody = ItemBody & { location_code?: string; qty_placed?: number; override_reason?: string }

/** Cek lokasi tujuan satu item (prototype `cekRack`): NG hanya QN-NG, Good hanya rack aktif, BR-04. */
export function checkPlacement(item: LocalRow, unit: LocalRow, code: string) {
  const upper = code.trim().toUpperCase()
  const isNg = unit.flag === 'NG'
  if (isNg) {
    const zone = zoneByCode(upper)
    if (!zone || zone.code !== NG_ZONE)
      return {
        ok: false as const,
        title: 'Barang NG Hanya ke Karantina NG',
        message: `Barang NG hanya boleh ditempatkan di zona Karantina NG (${NG_ZONE}).`
      }
    return { ok: true as const, zone, bin: null, override: false }
  }
  if (upper === NG_ZONE)
    return {
      ok: false as const,
      title: 'Barang Good Bukan ke Karantina NG',
      message: `Zona ${NG_ZONE} khusus barang NG. Barang Good ditaruh di rak.`
    }
  const bin = binByCode(upper)
  if (!bin || bin.is_active === false)
    return {
      ok: false as const,
      title: 'Bukan Rak Penyimpanan',
      message: `Kode ${code} bukan rak penyimpanan RM yang terdaftar.`
    }
  const foreign = foreignMaterialAt(bin.id, idOf(unit.material_id))
  if (foreign)
    return {
      ok: false as const,
      title: 'Posisi Sudah Berisi Material Lain',
      message: `Slot ${bin.code} sudah dipakai material ${foreign.code}. Satu posisi hanya boleh berisi satu jenis material — tidak ada opsi override.`
    }
  const rack = rackOf(bin)
  const restriction = idOf(rack?.category_restriction_id)
  const material = findRow('mt_material', idOf(unit.material_id))
  const recommended = idOf(item.recommended_rack_bin_id) === bin.id
  if (!recommended && restriction && restriction !== idOf(material?.category_id)) {
    const group = findRow('mt_material_group', restriction)
    return {
      ok: false as const,
      title: 'Rak Diblokir',
      message: `Rak ${bin.code} diblokir untuk kategori material ini (khusus ${group?.name ?? group?.code}). Tidak bisa di-override.`
    }
  }
  return { ok: true as const, zone: zoneOfBin(bin) ?? null, bin, override: !recommended }
}

/** Tahap 2: taruh satu item di rack / zona dengan jumlah yang benar-benar ditaruh. */
export function placePutawayItem(body: PlaceBody) {
  const { item, task, unit } = requireItem(body)
  const tobacco = isTobaccoUnit(unit)
  if (isPlaced(item)) fail('Barang ini sudah tersimpan.', 'ERR_WORKFLOW_STATE_MISMATCH', 422)
  if (!item.picked_at)
    fail(
      tobacco ? 'Stiker barang ini belum discan di tahap 1.' : 'Barang belum diangkat dari Area QC RM.',
      'ERR_WORKFLOW_STATE_MISMATCH',
      422
    )
  if (
    tobacco &&
    String(body.location_code ?? '')
      .trim()
      .toUpperCase() === QC_AREA_ZONE
  )
    fail(`${TOBACCO_NOT_QC_AREA} Scan rack tujuan.`)
  const check = checkPlacement(item, unit, String(body.location_code ?? ''))
  if (!check.ok) fail(`${check.title} — ${check.message}`)
  const reason = String(body.override_reason ?? '').trim()
  if (check.override && !reason) fail('Alasan wajib diisi.')
  const max = Number(item.qty_assigned ?? 1)
  const qty = Number(body.qty_placed ?? max)
  if (!Number.isInteger(qty) || qty < 1 || qty > max) fail(`Jumlah harus antara 1 dan ${max}.`)
  /*
   * Tembakau (keputusan user 2026-10-21 #1, usulan): stok dicatat per label di posisi rak dan satu label tidak pernah
   * dipecah ke dua rak. Label ditaruh utuh; taruh "sebagian" = sebagian LABEL tugas, sisa label tetap item terbuka dan
   * bisa dibawa ke rak lain ("Sisa ke Rack Lain" / override). Tidak ada aturan jumlah pas (Q-08) dan tidak ada `qty_short`.
   * Perilaku lama (item dipecah, unit dicatat di rak terakhir — konflik D61) dihapus.
   */
  if (tobacco && qty !== max)
    fail(
      `Satu label tidak boleh dipecah ke dua rack — taruh label ${unit.qr_code} utuh (${max} ${unit.unit_big_snapshot ?? ''}).`
    )
  if (tobacco) {
    const elsewhere = readTable('rm_putaway_task_item').find(
      row =>
        idOf(row.unit_id) === unit.id &&
        row.id !== item.id &&
        isPlaced(row) &&
        idOf(row.actual_rack_bin_id) !== String(check.bin?.id ?? '')
    )
    if (elsewhere)
      fail(
        `Label ${unit.qr_code} sudah tercatat di ${findRow('mt_rack_bin', idOf(elsewhere.actual_rack_bin_id))?.code ?? 'rack lain'} — satu label hanya punya satu posisi.`
      )
  }

  const now = new Date().toISOString()
  const isNg = unit.flag === 'NG'
  const code = String(check.bin?.code ?? check.zone?.code ?? '')
  updateRow('rm_putaway_task_item', item.id, {
    actual_rack_bin_id: check.bin?.id ?? null,
    actual_warehouse_zone_id: check.zone?.id ?? null,
    qty_placed: qty,
    qty_short: !tobacco && qty < max ? max - qty : null,
    override_reason: check.override ? reason : null,
    ...auditFieldsForUpdate()
  })

  /* Unit masuk stok hanya setelah SELURUH bagiannya (tugas lintas operator) ditaruh. */
  const parts = readTable('rm_putaway_task_item').filter(row => idOf(row.unit_id) === unit.id)
  const allPlaced = parts.every(row => isPlaced(row))
  const placedQty = parts.reduce((sum, row) => sum + Number(row.qty_placed ?? 0), 0)
  const perBig = Number(unit.small_per_big_snapshot ?? 1)
  updateRow('rm_unit', unit.id, {
    rack_bin_id: check.bin?.id ?? null,
    warehouse_zone_id: check.zone?.id ?? null,
    ...(allPlaced
      ? {
          unit_status: isNg ? 'NG_IN_QUARANTINE' : 'IN_STOCK',
          qty_small: placedQty * perBig,
          stored_at: unit.stored_at ?? now
        }
      : {}),
    ...auditFieldsForUpdate()
  })
  insertRow('stock_movement', {
    ...auditFieldsForCreate(),
    id: newId(),
    ts: now,
    movement_type: isNg ? 'NG_PLACEMENT' : 'PUTAWAY',
    unit_id: unit.id,
    qty_small: qty * perBig,
    from_rack_bin_id: null,
    to_rack_bin_id: check.bin?.id ?? null,
    /* Tembakau tidak pernah di Area QC RM — sumber perpindahan kosong (lokasi terima). */
    from_warehouse_zone_id: tobacco ? null : (zoneByCode(QC_AREA_ZONE)?.id ?? null),
    to_warehouse_zone_id: check.zone?.id ?? null,
    doc_ref: task.task_no
  })
  if (isNg) {
    const ticket = readTable('ng_ticket').find(row => idOf(row.unit_id) === unit.id)
    if (ticket)
      updateRow('ng_ticket', ticket.id, {
        placement_rack_bin_id: null,
        placement_warehouse_zone_id: check.zone?.id ?? null,
        ...auditFieldsForUpdate()
      })
  }
  const material = findRow('mt_material', idOf(unit.material_id))
  txLog(
    isNg ? 'RM NG Placement' : 'RM Putaway',
    'IRM-07',
    isNg ? 'ng-placement' : 'putaway',
    String(unit.qr_code),
    `${material?.code} ${qty} ${unit.unit_big_snapshot ?? ''} → ${code}${isNg ? ' (Karantina NG)' : ''}${check.override ? ` (override: ${reason})` : ''}`
  )

  /* Assignment → In Storage saat unit Good IN_STOCK dan unit NG (bila ada) di Karantina NG. */
  const storedAssignment = allPlaced ? storeAssignmentIfComplete(unit.id) : null

  /* Tugas selesai saat semua item ditaruh; pallet dilepas saat seluruh isinya berdiri. */
  const items = readTable('rm_putaway_task_item').filter(row => idOf(row.task_id) === task.id)
  const taskDone = items.every(row => isPlaced(row))
  updateRow('rm_putaway_task', task.id, {
    task_status: taskDone ? 'COMPLETED' : 'IN_PROGRESS',
    ...auditFieldsForUpdate()
  })
  const releasedPallet = releasePalletIfEmpty(unit)
  /* Retur WIP / tembakau yang seluruh unitnya sudah tersimpan otomatis "In Storage" (tanpa menunggu Selesaikan Tugas). */
  if (taskDone) closeReturnsAfterPutaway()

  return {
    task_item_id: item.id,
    unit_qr: unit.qr_code,
    location_code: code,
    qty_placed: qty,
    qty_short: !tobacco && qty < max ? max - qty : 0,
    /* Label tembakau yang belum ditaruh tetap item terbuka di tugas ini. */
    open_labels: tobacco
      ? readTable('rm_putaway_task_item').filter(row => idOf(row.task_id) === task.id && !isPlaced(row)).length
      : 0,
    override: check.override,
    unit_status: allPlaced ? (isNg ? 'NG_IN_QUARANTINE' : 'IN_STOCK') : unit.unit_status,
    task_status: taskDone ? 'COMPLETED' : 'IN_PROGRESS',
    assignment_stored: storedAssignment,
    pallet_released: releasedPallet
  }
}

function storeAssignmentIfComplete(unitId: string) {
  const assignment = readTable('rm_qc_assignment').find(
    row => (idOf(row.unit_id) === unitId || idOf(row.ng_unit_id) === unitId) && statusCodeOf(row) === 'QC_DONE'
  )
  if (!assignment) return null
  const goodOk = !assignment.unit_id || findRow('rm_unit', idOf(assignment.unit_id))?.unit_status === 'IN_STOCK'
  const ngOk =
    !assignment.ng_unit_id || findRow('rm_unit', idOf(assignment.ng_unit_id))?.unit_status === 'NG_IN_QUARANTINE'
  if (!goodOk || !ngOk) return null
  const workflow = readTable('wml_workflow').find(row => row.code === 'WF-QC-01')
  const action = readTable('wml_action').find(
    row => idOf(row.workflow_id) === workflow?.id && row.name === 'ACT_QC_STORE_ALL'
  )
  const actor = readTable('wml_actor').find(
    row => idOf(row.workflow_id) === workflow?.id && row.code === 'ACT_QC_OP_RM'
  )
  setSubmission({
    workflow_id: String(workflow?.id),
    submission_id: String(assignment.submission_id),
    action_id: String(action?.id),
    actor_id: String(actor?.id),
    native_document_payload: { path: 'rm_qc_assignment', payload: { qc_status: 'IN_STORAGE' } }
  })
  return String(assignment.assignment_no)
}

function releasePalletIfEmpty(unit: LocalRow) {
  const entry = findRow('rm_quarantine_entry', idOf(unit.quarantine_entry_id))
  const pallet = entry?.pallet_id ? findRow('pallet', idOf(entry.pallet_id)) : undefined
  if (!pallet || pallet.pallet_status !== 'IN_QC') return null
  const entries = readTable('rm_quarantine_entry').filter(row => idOf(row.pallet_id) === pallet.id)
  const allDone = entries.every(row => {
    if (!row.is_qc_completed) return false
    const units = readTable('rm_unit').filter(u => idOf(u.quarantine_entry_id) === row.id)
    return units.length > 0 && units.every(u => u.unit_status === 'IN_STOCK' || u.unit_status === 'NG_IN_QUARANTINE')
  })
  if (!allDone) return null
  updateRow('pallet', pallet.id, {
    pallet_status: 'EMPTY',
    cycle_count: Number(pallet.cycle_count ?? 0) + 1,
    ...auditFieldsForUpdate()
  })
  readTable('rm_pallet_content')
    .filter(row => idOf(row.pallet_id) === pallet.id)
    .forEach(row => deleteRow('rm_pallet_content', row.id))
  txLog(
    'RM Putaway',
    'IRM-07',
    'pallet-released',
    String(pallet.code),
    `Seluruh isi pallet ${pallet.code} selesai ditempatkan — pallet kembali kosong`
  )
  return String(pallet.code)
}

/** "Rack Penuh": sisa item yang direkomendasikan ke rack itu dialihkan ke posisi pengganti (alasan wajib). */
export function rackFull(body: { task_id?: string; rack_bin_code?: string; reason?: string }) {
  const task = findRow('rm_putaway_task', body.task_id)
  if (!task) fail('Tugas tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  requireOperatorOwner(task?.operator_id)
  const reason = String(body.reason ?? '').trim()
  /* Tembakau: sisa label boleh ke rak lain tanpa alasan (keputusan Q-08 + 2026-10-21 #1); RM lain tetap wajib alasan. */
  const tobacco = isTobaccoTask(String(task?.id))
  if (!reason && !tobacco) fail('Alasan wajib diisi.')
  const full = binByCode(String(body.rack_bin_code ?? '').toUpperCase())
  if (!full) fail('Rak tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  const items = readTable('rm_putaway_task_item').filter(
    row => idOf(row.task_id) === task?.id && !isPlaced(row) && idOf(row.recommended_rack_bin_id) === full?.id
  )
  if (!items.length)
    fail(tobacco ? 'Tidak ada label yang masih menuju rak ini.' : 'Tidak ada tumpukan yang masih menuju rak ini.')
  const unit = findRow('rm_unit', idOf(items[0].unit_id)) as LocalRow
  const replacement = recommendRackBin(idOf(unit.material_id), [full?.id as string])
  if (!replacement) fail('Tidak ada rack pengganti — semua rack yang sesuai sudah terpakai. Hubungi Supervisor WH RM.')
  items.forEach(item =>
    updateRow('rm_putaway_task_item', item.id, {
      recommended_rack_bin_id: replacement.id,
      recommended_warehouse_zone_id: zoneOfBin(replacement)?.id ?? null,
      ...auditFieldsForUpdate()
    })
  )
  txLog(
    'RM Putaway',
    'IRM-07',
    tobacco ? 'putaway-sisa-tembakau' : 'putaway-rack-penuh',
    String(task?.task_no),
    tobacco
      ? `${items.length} label tembakau belum ditaruh di ${full?.code} — dialihkan ke ${replacement.code}${reason ? ` (${reason})` : ''}`
      : `Rack ${full?.code} penuh (${reason}) — ${items.length} tumpukan dialihkan ke ${replacement.code}`
  )
  return { from: full?.code, to: replacement.code, moved: items.length }
}

/** "Selesaikan Tugas": tanpa sisa → COMPLETED; dengan sisa → catatan wajib, tugas tetap terbuka. */
export function finishPutawayTask(body: { task_id?: string; note?: string }) {
  const task = findRow('rm_putaway_task', body.task_id)
  if (!task) fail('Tugas tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  requireOperatorOwner(task?.operator_id)
  const items = readTable('rm_putaway_task_item').filter(row => idOf(row.task_id) === task?.id)
  const remaining = items.filter(row => !isPlaced(row))
  const note = String(body.note ?? '').trim()
  if (remaining.length && !note) fail('Catatan wajib bila tetap diselesaikan dengan sisa.')
  updateRow('rm_putaway_task', task?.id as string, {
    task_status: remaining.length ? 'IN_PROGRESS' : 'COMPLETED',
    note: note || task?.note || null,
    ...auditFieldsForUpdate()
  })
  if (remaining.length)
    txLog(
      'RM Putaway',
      'IRM-07',
      'putaway-sisa',
      String(task?.task_no),
      `${remaining.length} tumpukan belum disimpan — ${note}`
    )
  if (!remaining.length) closeReturnsAfterPutaway()
  return { task_id: task?.id, remaining: remaining.length, task_status: remaining.length ? 'IN_PROGRESS' : 'COMPLETED' }
}

/* -------------------------------------------------------------- routes */

/* ------------------------------------------------- IRM-08 koreksi penempatan */

type RelocateBody = {
  unit_id?: string
  actual_location_code?: string
  new_location_code?: string
  /** Wajib bila lokasi aktual = catatan sistem ("Tetap Pindahkan"). */
  reason?: string
}

/** Rak sah untuk koreksi: posisi rak aktif (zona khusus QC/Karantina bukan tempat simpan biasa). */
function requireBin(code: unknown, label: string) {
  const upper = String(code ?? '')
    .trim()
    .toUpperCase()
  const bin = binByCode(upper)
  if (!bin || bin.is_active === false)
    fail(`Bukan Lokasi Terdaftar — Kode ${upper || '—'} bukan posisi rak RM yang terdaftar (${label}).`)
  return bin
}

/**
 * `POST /api/v1/wms/inbound-rm/misplacement-relocate` (BE M01.C04.P05.F01, CUSTOM-NESTJS) — koreksi tiga scan
 * untuk SATU unit In Stock: lokasi tercatat → lokasi aktual (tempat ditemukan) → lokasi baru. Berdiri sendiri,
 * tidak terikat stock opname. Menulis lokasi baru pada unit, `stock_movement` RELOCATION (bila rak berubah) dan
 * `tx_log misplacement-fix` (tercatat → aktual → baru, petugas).
 */
/** Cermin server `MISPLACEMENT_CORRECT_ROLES` (IRM-08, D4): Operator & Admin WH RM. */
export const MISPLACEMENT_CORRECT_ROLES = ['operator_wh_rm', 'admin_wh_rm'] as const

export function relocateUnit(body: RelocateBody) {
  const me = currentUserId()
  const roles = roleCodesOf(me)
  if (!MISPLACEMENT_CORRECT_ROLES.some(code => roles.includes(code)))
    fail('Koreksi hanya untuk Operator/Admin WH RM.', 'ERR_UNAUTHORIZED_ROLE', 403)
  const unit = findRow('rm_unit', String(body.unit_id ?? ''))
  if (!unit) fail('Unit tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  if (unit.unit_status !== 'IN_STOCK')
    fail(
      `Unit Tidak Bisa Dikoreksi — ${unit.qr_code} berstatus ${unit.unit_status}. Koreksi hanya untuk unit In Stock.`
    )
  const material = findRow('mt_material', idOf(unit.material_id))
  const recorded = findRow('mt_rack_bin', idOf(unit.rack_bin_id))
  const actual = requireBin(body.actual_location_code, 'lokasi aktual')
  const target = requireBin(body.new_location_code, 'lokasi baru')
  const reason = String(body.reason ?? '').trim()
  if (recorded && actual.id === recorded.id && !reason)
    fail('Alasan Wajib — Lokasi aktual sama dengan catatan sistem; isi alasan memindahkan unit ini.')

  /* BR-04 pada tujuan akhir: satu posisi satu material (material sama boleh menumpuk), kategori rak hard-block. */
  const foreign = foreignMaterialAt(target.id, idOf(unit.material_id))
  if (foreign)
    fail(
      `Posisi Dipakai Material Lain — Posisi ${target.code} sudah dipakai material ${foreign.code}. Satu posisi hanya boleh berisi satu jenis material.`
    )
  const restriction = idOf(rackOf(target)?.category_restriction_id)
  if (restriction && restriction !== idOf(material?.category_id)) {
    const group = findRow('mt_material_group', restriction)
    fail(
      `Rak Diblokir — Rak ${target.code} khusus ${group?.name ?? group?.code}; diblokir untuk material ini dan tidak bisa di-override.`
    )
  }

  const moved = recorded?.id !== target.id
  const docNo = nextDocNo('MSP', 'stock_movement', 'doc_ref')
  updateRow('rm_unit', String(unit.id), {
    rack_bin_id: target.id,
    warehouse_zone_id: idOf(rackOf(target)?.zone_id) || unit.warehouse_zone_id,
    ...auditFieldsForUpdate()
  })
  if (moved)
    insertRow('stock_movement', {
      ...auditFieldsForCreate(),
      id: newId(),
      ts: new Date().toISOString(),
      movement_type: 'RELOCATION',
      unit_id: unit.id,
      qty_small: Number(unit.qty_small ?? 0) || Number(unit.qty ?? 0),
      from_rack_bin_id: recorded?.id ?? null,
      to_rack_bin_id: target.id,
      from_warehouse_zone_id: unit.warehouse_zone_id ?? null,
      to_warehouse_zone_id: idOf(rackOf(target)?.zone_id) || null,
      doc_ref: docNo
    })
  txLog(
    'Misplacement Correction',
    'IRM-08',
    'misplacement-fix',
    String(unit.qr_code),
    `${unit.qr_code} — tercatat ${recorded?.code ?? '—'}, aktual ${actual.code} → lokasi baru ${target.code}${reason ? ` · alasan: ${reason}` : ''}`
  )
  return {
    unit_id: unit.id,
    unit_qr: unit.qr_code,
    recorded: recorded?.code ?? null,
    actual: actual.code,
    reason: reason || null,
    new_location: target.code,
    moved,
    doc_no: docNo
  }
}

const parse = <T>(data: unknown) => JSON.parse(String(data ?? '{}')) as T

const handle = (run: () => unknown) => {
  try {
    return ok(run())
  } catch (error) {
    return toErrorResponse(error)
  }
}

export function registerInboundRmOperatorHandlers(mock: MockAdapter) {
  mock
    .onPost('/api/v1/wms/inbound-rm/misplacement-relocate')
    .reply(config => handle(() => relocateUnit(parse(config.data))))
  mock
    .onPost('/api/v1/wms/qc-rm-karantina/workflow/qc-inspection-results-submission')
    .reply(config => handle(() => saveQcDraft(parse(config.data))))
  mock
    .onPost('/api/v1/wms/inbound-receiving/qc-package/approve-print')
    .reply(config => handle(() => approvePrintQcPackage(parse(config.data))))
  mock
    .onPost('/api/v1/wms/inbound-receiving/qc-package/return')
    .reply(config => handle(() => returnQcPackage(parse(config.data))))
  mock
    .onPost('/api/v1/wms/putaway-execution/pick-item')
    .reply(config => handle(() => pickPutawayItem(parse(config.data))))
  mock
    .onPost('/api/v1/wms/putaway-execution/bring-to-rack')
    .reply(config => handle(() => bringToRack(parse(config.data))))
  mock
    .onPost('/api/v1/wms/putaway-execution/place-item')
    .reply(config => handle(() => placePutawayItem(parse(config.data))))
  mock.onPost('/api/v1/wms/putaway-execution/rack-full').reply(config => handle(() => rackFull(parse(config.data))))
  mock
    .onPost('/api/v1/wms/putaway-execution/finish-task')
    .reply(config => handle(() => finishPutawayTask(parse(config.data))))
}
